// =====================================================
// COMPOSANT: CouponInput
// Date: 2025-11-28
// Description: Composant réutilisable pour la saisie et validation de coupons
// =====================================================

import React, { useState } from 'react';
import { Tag, X, Loader2, CheckCircle, AlertCircle, Percent, Gift } from 'lucide-react';
import { validateCoupon, type ModuleType, type Coupon } from '../../services/couponService';
import { toast } from 'sonner';

interface CouponInputProps {
  module: ModuleType;
  amount: number;
  userEmail: string;
  onApply: (discountAmount: number, couponCode: string, coupon: Coupon) => void;
  onRemove: () => void;
  appliedCoupon?: string | null;
  disabled?: boolean;
  className?: string;
}

const CouponInput: React.FC<CouponInputProps> = ({
  module,
  amount,
  userEmail,
  onApply,
  onRemove,
  appliedCoupon,
  disabled = false,
  className = ''
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<number | null>(null);
  const [appliedCouponData, setAppliedCouponData] = useState<Coupon | null>(null);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setValidationError('Veuillez entrer un code promo');
      return;
    }

    if (!userEmail) {
      setValidationError('Email requis pour appliquer un code promo');
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      const result = await validateCoupon(couponCode, module, amount, userEmail);

      if (result.valid && result.coupon && result.discount_amount !== undefined) {
        setAppliedDiscount(result.discount_amount);
        setAppliedCouponData(result.coupon);
        onApply(result.discount_amount, couponCode.toUpperCase().trim(), result.coupon);
        toast.success(`Code promo appliqué ! -${result.discount_amount.toLocaleString('fr-FR')} FCFA`);
      } else {
        setValidationError(result.error || 'Code promo invalide');
        toast.error(result.error || 'Code promo invalide');
      }
    } catch (error) {
      console.error('Erreur lors de la validation du coupon:', error);
      setValidationError('Erreur lors de la validation');
      toast.error('Erreur lors de la validation du code promo');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedDiscount(null);
    setAppliedCouponData(null);
    setValidationError(null);
    onRemove();
    toast.info('Code promo retiré');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isValidating && !appliedCoupon) {
      e.preventDefault();
      handleApplyCoupon();
    }
  };

  // Si un coupon est déjà appliqué
  if (appliedCoupon || appliedDiscount !== null) {
    return (
      <div className={`bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-800/50 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-green-700 dark:text-green-300">
                  {appliedCoupon || couponCode.toUpperCase()}
                </span>
                {appliedCouponData && (
                  <span className="text-xs bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-200 px-2 py-0.5 rounded-full">
                    {appliedCouponData.discount_type === 'percentage' 
                      ? `${appliedCouponData.discount_value}%` 
                      : `${appliedCouponData.discount_value.toLocaleString('fr-FR')} FCFA`}
                  </span>
                )}
              </div>
              <p className="text-sm text-green-600 dark:text-green-400">
                Réduction: -{appliedDiscount?.toLocaleString('fr-FR')} FCFA
              </p>
            </div>
          </div>
          {!disabled && (
            <button
              onClick={handleRemoveCoupon}
              className="p-2 text-green-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Retirer le code promo"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <Gift className="w-4 h-4" />
        <span>Code promo</span>
      </div>
      
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value.toUpperCase());
              setValidationError(null);
            }}
            onKeyPress={handleKeyPress}
            placeholder="Entrez votre code"
            disabled={disabled || isValidating}
            className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all
              ${validationError 
                ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
            `}
          />
        </div>
        
        <button
          onClick={handleApplyCoupon}
          disabled={disabled || isValidating || !couponCode.trim()}
          className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2
            ${disabled || isValidating || !couponCode.trim()
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#D4AF37] to-[#B8941F] hover:from-[#B8941F] hover:to-[#9A7B19] text-white shadow-md hover:shadow-lg'
            }
          `}
        >
          {isValidating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="hidden sm:inline">Vérification...</span>
            </>
          ) : (
            <>
              <Percent className="w-4 h-4" />
              <span className="hidden sm:inline">Appliquer</span>
            </>
          )}
        </button>
      </div>

      {validationError && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{validationError}</span>
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Entrez votre code promo pour bénéficier d'une réduction
      </p>
    </div>
  );
};

export default CouponInput;
